<template>
  <div>
    <div class="search-header-container">
      <div v-show="!showFilters" class="toggle">
        <FiltersToggle />
      </div>
      <div class="title-description-container">
        <div class="title">
          <h1>{{ actualSearchTitle }}</h1>
        </div>
        <div class="description">
          <h2>{{ actualSearchDescription }}</h2>
        </div>
      </div>
    </div>
    <div class="filters-container">
      <div v-if="tags.length > 0" class="tags-container">
        <span class="relateds-title">{{ $t('main.searchResult.searchHeader.text') }}</span>
        <div v-for="(t, i) in tags" :key="i" class="related-link">
          <nuxt-link v-if="t.type == 'internal'" :to="internalLink(t.dimension, t.slug)">{{
            t.text
          }}</nuxt-link>
          <a v-else :href="t.url" target="_blank">{{ t.text }}</a
          ><span v-if="i < tags.length - 1">,&nbsp;</span>
        </div>
      </div>
      <div class="sort-by">
        <SmallPaginator
          v-if="defaultSort"
          v-bind="properties"
          @change-page="changePage"
          @next-page="nextPage"
          @prev-page="prevPage"
        />
        <SortBy
          v-else
          v-bind="properties"
          @change-page="changePage"
          @next-page="nextPage"
          @prev-page="prevPage"
        />
      </div>
    </div>
  </div>
</template>

<script>
import FiltersToggle from '@/components/SearchResults/FiltersToggle';
import { mapState, mapGetters } from 'vuex';
import SortBy from '@/components/SearchResults/SortBy';
import SmallPaginator from '@/components/SearchResults/SmallPaginator';

export default {
  components: {
    FiltersToggle,
    SmallPaginator,
    SortBy
  },
  props: {
    defaultSort: {
      type: String,
      required: false,
      default: ''
    },
    forceExperience: {
      type: String,
      required: false,
      default: ''
    }
  },
  computed: {
    ...mapState('search', ['currentPage', 'totalPages']),
    ...mapState('dimensions', ['dimensionOptions']),
    ...mapState('searchBar', ['actualSearch']),
    ...mapGetters('dimensions', [
      'getActualDimensionName',
      'getDimensionByName',
      'getPaginationDimension'
    ]),
    ...mapGetters('search', ['getQuery']),
    ...mapGetters('experience', ['getExperience']),
    ...mapGetters('searchBar', ['getActualSearch']),
    actualSearchTitle() {
      return this.$store.getters['search/getSearchTitle'];
    },
    actualSearchDescription() {
      return this.$store.getters['search/getSearchDescription'];
    },
    showFilters() {
      return this.$store.getters['search/getFiltersVisibility'];
    },
    tags() {
      return this.$store.getters['search/getSearchRelatedTags'];
    },
    nextPageLink() {
      const dimName = this.getPaginationDimension
        ? this.getPaginationDimension
        : this.getActualDimensionName;

      return this.$urlBuilder('search', {
        path: this.getDimensionByName(dimName, this.getExperience, false).path,
        keyword: this.actualSearch,
        page: this.currentPage + 1,
        query: this.query
      });
    },
    prevPageLink() {
      const dimName = this.getPaginationDimension
        ? this.getPaginationDimension
        : this.getActualDimensionName;

      return this.$urlBuilder('search', {
        path: this.getDimensionByName(dimName, this.getExperience, false).path,
        keyword: this.actualSearch,
        page: this.currentPage < 1 ? false : this.currentPage - 1,
        query: this.query
      });
    },
    properties() {
      return {
        currentPage: this.currentPage,
        totalPages: this.totalPages,
        nextPageLink: this.nextPageLink,
        prevPageLink: this.prevPageLink,
        useNuxtLink: false,
        topPaginator: true
      };
    },
    searchDimension() {
      return this.getPaginationDimension
        ? this.getPaginationDimension
        : this.getActualDimensionName;
    },
    query() {
      return { ...this.$route.query, ...this.getQuery };
    }
  },
  methods: {
    changePage(page, e) {
      e.preventDefault();
      this.updateSearch(page, this.actualSearch);
    },
    nextPage(e) {
      e.preventDefault();
      this.updateSearch(this.currentPage + 1, this.actualSearch);
    },
    prevPage(e) {
      e.preventDefault();
      this.updateSearch(this.currentPage - 1, this.actualSearch);
    },
    async updateSearch(page, search) {
      const searchPromise = this.$store.dispatch('search/MAKE_SEARCH', {
        criteria: search,
        sort: this.defaultSort,
        page,
        experience: this.forceExperience
      });

      await Promise.all([searchPromise]);

      window.scrollTo(0, 0);
      const url = this.$urlBuilder('search', {
        path: this.getDimensionByName(this.searchDimension, this.getExperience, false).path,
        keyword: search,
        page
      });
      this.$store.commit('search/SET_SILENT_SEARCH', true);
      this.$router.push(url);
    },
    internalLink(dimension, slug) {
      return this.$urlBuilder('search', {
        path: dimension,
        keyword: slug
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.search-header-container .toggle {
  @include lg {
    display: block;
    position: fixed;
    bottom: 10px;
    left: 10px;
    z-index: 300;
  }
}

.search-header-container .title-description-container {
  flex: 1;
  margin-left: 10px;
  padding-right: 20px;
  @include sm {
    margin-left: 0;
    padding-right: 0;
  }
}
.search-header-container .title-description-container .title h1 {
  font-size: 18px;
  color: #2d2d2d;
  text-transform: lowercase;
}
.search-header-container .title-description-container .description h2 {
  font-size: 14px;
  color: $strongGray;
  text-transform: lowercase;
  font-weight: 400;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.relateds-title {
  color: #888888;
  text-transform: lowercase;
  font-weight: 400;
}
.tags-container {
  .related-link {
    a,
    span {
      color: var(--exp-color);
    }
    &:hover {
      filter: brightness(0.9);
    }
  }
  display: flex;
  align-items: center;
  justify-content: center;
  @include sm {
    font-size: 14px;
    justify-content: flex-start;
    margin-bottom: 10px;
  }
}
.tags-container .relateds-title {
  margin-right: 3px;
}
.filters-container {
  display: flex;
  margin: 10px 0;
  .sort-by {
    margin-left: auto;
    @include sm {
      margin: 0;
    }
  }
  @include sm {
    flex-direction: column;
  }
}

@media all and (min-width: 576px) {
  .search-header-container {
    display: flex;
    margin-top: 15px;
    margin-bottom: 15px;
    position: initial;
  }
}
</style>
